/* eslint-disable jsx-a11y/no-static-element-interactions */

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import TagsInput from 'react-tagsinput';
import StoresInput from './StoresInput/SIIndex';
import ZipsInput from './ZipsInput/ZIIndex';
import styles from 'styles/input.scss';
import S from '../styles.scss';
import CTIStyles from './CTIStyles.scss';

class CustomTaggedInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      storeZips: [],
      storeZipsDict: this.props.crew.store_zips_dictionary || { 'Additional Zip Codes': [] },
    };
  }

  componentDidMount = () => {
    const { storeZipsDict } = this.state;
    // If crew has a zip under a store that was removed by that store,-
    // remove the zip from that store in crew's zips (if not on any other store,-
    // move to 'Additional Zip Codes'
    this.moveZipsRemovedFromStores();
    // If crew has zips in 'Additional Zip Codes' check if any relevant stores cover any of these-
    // zips and fill the stores in the ui
    if (storeZipsDict['Additional Zip Codes'].length > 0) {
      const updatedSZD = this.filterAddZipsIntoStores(storeZipsDict);
      this.updateState(updatedSZD);
    } else this.updateState(storeZipsDict);
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { storeZipsDict } = this.state;
    const { workroomChange } = this.props;

    if (prevState.storeZipsDict !== storeZipsDict) {
      // Removes any zip from "Additional Zip Codes" that belongs to a store when store is populated
      this.onStorePopulate(storeZipsDict);
    }

    // Handle removal and addition of workrooms
    if (workroomChange && prevProps.workroomChange !== workroomChange) {
      // Returns the new storeZipsDict object after workroom is removed
      const szd = this.removeIrrelevantStores();
      // When a workroom is added and a zip in 'Additional Zip Codes' section belongs to a store-
      // that belongs to one of those workrooms, filter that zip into that store.
      const updatedSZD = this.filterAddZipsIntoStores(szd);
      this.updateState(updatedSZD);
    }
  }

  onStorePopulate = (storeZipsDict) => {
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    const storeZipCodesSet = new Set();
    Object.values(storeZipsDict).forEach((szdVal) => {
      if (!Array.isArray(szdVal)) {
        szdVal.storeZips.forEach(zip => storeZipCodesSet.add(zip));
      }
    });
    const storeZipCodesArr = Array.from(storeZipCodesSet);
    const additionalZipsCopy = storeZipsDict['Additional Zip Codes'].slice();
    let zipIncluded = false;
    storeZipsDict['Additional Zip Codes'].forEach((zip) => {
      if (storeZipCodesArr.includes(zip)) {
        zipIncluded = true;
        additionalZipsCopy.splice(additionalZipsCopy.indexOf(zip), 1);
      }
    });
    storeZipsDictReplica['Additional Zip Codes'] = additionalZipsCopy;
    if (zipIncluded) this.updateState(storeZipsDictReplica);
  }

  // Determines which function to call based on if a zip is added or removed from the-
  // "Additional Zips" section
  onAddtlZipChange = (additionalZips, targetZips) => {
    // can be an array of multiple zips if copy/pasted
    const zipsToAdd = targetZips.filter(zip => (additionalZips.includes(zip)));
    // if zip(s) is added, add to storeZipsDict object
    // else if zip is removed, remove from 'Additional Zips' section of storeZipsDict
    if (zipsToAdd.length > 0) this.addToStoreZipsDict(zipsToAdd);
    else this.removeFromStoreZipsDict(targetZips[0]);
  };

  setStoreZips = (storeZipsDict) => {
    const storeZipsArr = [];
    const crewWorkrooms = this.getRelevantWorkrooms();
    crewWorkrooms.forEach(w => w.store_with_zips.forEach((swz) => {
      const { store } = swz;
      const matchedZips = storeZipsDict[store.name] ? storeZipsDict[store.name].storeZips : [];
      const uniqueMatchedZips = [...new Set(matchedZips)];
      storeZipsArr.push({
        storeNum: store.number,
        storeName: store.name,
        coveredZips: [...uniqueMatchedZips],
      });
    }));
    return storeZipsArr;
  };

  // Get all workrooms associated with the crew
  getRelevantWorkrooms = () => {
    const { crew, allWorkrooms } = this.props;
    let crewWorkrooms = [];
    if (typeof crew.workrooms[0] === 'number') {
      crewWorkrooms = allWorkrooms.filter(workroom =>
        crew.workrooms.includes(workroom.id));
    }
    // When adding/removing workrooms on form, workrooms come back as an object
    if (typeof crew.workrooms[0] === 'object') {
      const workroomsArr = crew.workrooms.map(workroom => workroom.value);
      crewWorkrooms = allWorkrooms.filter(workroom =>
        workroomsArr.includes(workroom.id));
    }
    return crewWorkrooms;
  };

  moveZipsRemovedFromStores = () => {
    const { storeZipsDict } = this.state;
    const szdReplica = Object.assign({}, storeZipsDict);
    const crewWorkrooms = this.getRelevantWorkrooms();
    crewWorkrooms.forEach(w => w.store_with_zips.forEach((swz) => {
      const { store } = swz;
      if (szdReplica[store.name]) {
        const zipsToRemoveFromStore = [];
        szdReplica[store.name].storeZips.forEach((zip) => {
          if (!store.covered_zip_codes.includes(zip)) zipsToRemoveFromStore.push(zip);
        });
        const newStoreZips = szdReplica[store.name].storeZips.filter(zip =>
          !zipsToRemoveFromStore.includes(zip));
        szdReplica[store.name].storeZips = newStoreZips;
        this.addToStoreZipsDict(zipsToRemoveFromStore);
      }
    }));
    this.updateState(szdReplica);
  }

  filterAddZipsIntoStores = (storeZipsDict) => {
    const zipsToRemoveFromAddZips = new Set();
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    const additionalZips = storeZipsDictReplica['Additional Zip Codes'];
    const crewWorkrooms = this.getRelevantWorkrooms();
    crewWorkrooms.forEach(w => w.store_with_zips.forEach((swz) => {
      const { store } = swz;
      store.covered_zip_codes.forEach((zip) => {
        let zipUsed = false;
        if (additionalZips.includes(zip)) {
          zipUsed = true;
          // create the entry in the storeZipsDict object if one doesn't already exist
          if (!storeZipsDictReplica[store.name]) {
            storeZipsDictReplica[store.name] = {
              storeNum: store.number,
              storeZips: [zip],
            };
          } else if (!storeZipsDictReplica[store.name].storeZips.includes(zip)) {
            storeZipsDictReplica[store.name].storeZips.push(zip);
            storeZipsDictReplica[store.name].storeZips.sort((a, b) => a - b);
          }
        }
        if (zipUsed === true) zipsToRemoveFromAddZips.add(zip);
      });
    }));
    // Remove the zips from "Additional Zip Code" section that belonged to other stores
    Array.from(zipsToRemoveFromAddZips).forEach((zip) => {
      const idx = additionalZips.indexOf(zip);
      additionalZips.splice(idx, 1);
    });
    return storeZipsDictReplica;
  }

  storeZipsEmpty = (storeZipsDict) => {
    const dictClone = Object.assign({}, storeZipsDict);
    delete dictClone['Additional Zip Codes'];
    return !Object.values(dictClone).some(s => (s.storeZips.length || []) > 0);
  }

  crewZipsEmpty = storeZipsDict => (Object.keys(storeZipsDict).length === 1 ||
    this.storeZipsEmpty(storeZipsDict)) &&
    storeZipsDict['Additional Zip Codes'].length < 1;

  // Add back all of the chosen store's zips to storeZipsDict and crew.covered_zip_codes
  addChosenStoresZips = (storeName) => {
    const { storeZipsDict } = this.state;
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    const crewWorkrooms = this.getRelevantWorkrooms();
    for (let i = 0; i < crewWorkrooms.length; i += 1) {
      const workroom = crewWorkrooms[i];
      for (let j = 0; j < workroom.store_with_zips.length; j += 1) {
        const stores = workroom.store_with_zips;
        if (stores[j].store.name === storeName) {
          const { store: { number, covered_zip_codes } } = stores[j];
          const storesCoveredZipsCopy = covered_zip_codes.slice();
          if (storeZipsDictReplica[storeName]) {
            storeZipsDictReplica[storeName].storeZips = storesCoveredZipsCopy;
          } else {
            storeZipsDictReplica[storeName] = {
              storeNum: number,
              storeZips: storesCoveredZipsCopy,
            };
          }
        }
      }
    }
    this.updateState(storeZipsDictReplica);
  };

  // Remove every occurence of a single zip code
  removeZipCode = (zip) => {
    const { storeZipsDict } = this.state;
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    Object.values(storeZipsDictReplica).forEach((szdVal) => {
      if (!Array.isArray(szdVal) && szdVal.storeZips.includes(zip)) {
        const zipIndex = szdVal.storeZips.indexOf(zip);
        szdVal.storeZips.splice(zipIndex, 1);
      }
    });
    this.updateState(storeZipsDictReplica);
  };

  // When a zip is typed or copy/pasted into "Additional Zip Codes" section, it'll filter into the-
  // respective store, if the store has that zip in their covered_zip_codes, otherwise if no-
  // store has the zip, it gets added to "Additional Zips"
  addToStoreZipsDict = (zips) => {
    const { storeZipsDict } = this.state;
    const zipsArr = Array.isArray(zips) ? zips : [zips];
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    const crewWorkrooms = this.getRelevantWorkrooms();
    zipsArr.forEach((zip) => {
      let zipBelongsToStore = false;
      crewWorkrooms.forEach(w => w.store_with_zips.forEach((swz) => {
        const { store } = swz;
        // if zip belongs to a store, add it to one of the storeZipsDict store's entries
        if (store.covered_zip_codes.includes(zip)) {
          zipBelongsToStore = true;
          // create the entry in the storeZipsDict object if one doesn't already exist
          if (!storeZipsDictReplica[store.name]) {
            storeZipsDictReplica[store.name] = {
              storeNum: store.number,
              storeZips: [zip],
            };
          } else if (!storeZipsDictReplica[store.name].storeZips.includes(zip)) {
            storeZipsDictReplica[store.name].storeZips.push(zip);
            storeZipsDictReplica[store.name].storeZips.sort((a, b) => a - b);
          }
        }
      }));
      // if zip does not belong to a store, add to the 'Additional Zip Codes' section
      const szdrAddZips = storeZipsDictReplica['Additional Zip Codes'];
      if (!zipBelongsToStore && !szdrAddZips.includes(zip)) szdrAddZips.push(zip);
    });
    this.updateState(storeZipsDictReplica);
  };

  // Remove all of the chosen store's zips from storeZipsDict by deleting entire entry
  removeChosenStoresZips = (storeName) => {
    const { storeZipsDict } = this.state;
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    delete storeZipsDictReplica[storeName];
    this.updateState(storeZipsDictReplica);
  };

  // Grabs all the zip codes from the storeZipsDict read object and updates-
  // the crew's covered_zip_codes and storeZipsDict object in the state
  updateState = (newStoreZipsDict) => {
    const zipsFromStoreZipsDictSet = new Set();
    Object.values(newStoreZipsDict).forEach((szdVal) => {
      if (!Array.isArray(szdVal)) {
        szdVal.storeZips.forEach(zip => zipsFromStoreZipsDictSet.add(zip));
      } else {
        szdVal.forEach(zip => zipsFromStoreZipsDictSet.add(zip));
      }
    });
    const zipsFromStoreZipsDictArr = Array.from(zipsFromStoreZipsDictSet)
                                          .sort((a, b) => a - b);
    const newStoreZips = this.setStoreZips(newStoreZipsDict);
    this.setState({
      storeZipsDict: newStoreZipsDict,
      storeZips: newStoreZips,
    }, () => {
      const updateObj = { zipsFromStoreZipsDictArr, newStoreZipsDict };
      this.props.onCrewZipChange('covered_zip_codes', updateObj);
    });
  };

  // Removes a zip from the "Additional Zips" section, thereby removing it from storeZipsDict-
  // and from crew.covered_zip_codes
  removeFromStoreZipsDict = (zip) => {
    const { storeZipsDict } = this.state;
    const storeZipsDictReplica = Object.assign({}, storeZipsDict);
    const targetZipIndex = storeZipsDictReplica['Additional Zip Codes'].indexOf(zip);
    storeZipsDictReplica['Additional Zip Codes'].splice(targetZipIndex, 1);
    this.updateState(storeZipsDictReplica);
  };

  removeIrrelevantStores = () => {
    const crewWorkrooms = this.getRelevantWorkrooms();
    const allStores = [];
    crewWorkrooms.forEach(w => w.store_with_zips.forEach((swz) => {
      allStores.push(swz.store.name);
    }));
    const storeZipsDictReplica = Object.assign({}, this.state.storeZipsDict);
    const storeZipsDictStores = Object.keys(storeZipsDictReplica);
    storeZipsDictStores.forEach((szdStore) => {
      if (!allStores.includes(szdStore) &&
        szdStore !== 'Additional Zip Codes') delete storeZipsDictReplica[szdStore];
    });
    return storeZipsDictReplica;
  };

  removeAllZips = () => {
    const emptySZD = { 'Additional Zip Codes': [] };
    this.updateState(emptySZD);
  }

  render = () => {
    const { storeZipsDict, storeZips } = this.state;
    // TODO: Hiding TWD - Remove "eslint-disable-next-line" when Dev up to date
    // eslint-disable-next-line
    const { label, id, placeholder, error, crew: { traveling_zip_codes = [] } } = this.props;
    const wrapperStyles = error ? `${S.wrapper} ${styles.errored}` : S.wrapper;
    const labelStyles = error ? `${S.label} ${S.errorLabel}` : S.label;
    const errStyles = `${styles.error} ${S.error}`;
    const inputStyles = `${styles.inputElement} ${S.element}`;
    const pasteSplit = data => data.split(/[ ,]+/).map(d => d.trim());
    return (<div className={wrapperStyles}>
      <label className={labelStyles} htmlFor={id}>{label}</label>
      <div className={CTIStyles.contentContainer}>
        {traveling_zip_codes.length > 0 && <div className={CTIStyles.additionalZipsContainer}>
          <div className={CTIStyles.additionalZipsHeader}>Traveling Zip Codes</div>
          <TagsInput
            disabled
            onlyUnique
            id={id}
            value={traveling_zip_codes.sort()}
            onChange={() => {}}
            className={inputStyles}
            tagProps={{ className: S.tag, classNameRemove: 'react-tagsinput-remove' }}
            inputProps={{ placeholder, className: S.input }}
          />
        </div>}
        <div className={CTIStyles.additionalZipsContainer}>
          <div className={CTIStyles.additionalZipsHeaderAndRemoveAll}>
            <div className={CTIStyles.additionalZipsHeader}>Additional Zip Codes</div>
            <div
              className={CTIStyles.removeAll}
              onClick={() => this.removeAllZips()}
            >Remove All</div>
          </div>
          <TagsInput
            onlyUnique
            id={id}
            value={storeZipsDict['Additional Zip Codes'].sort((a, b) => a - b)}
            onChange={this.onAddtlZipChange}
            addOnPaste
            pasteSplit={pasteSplit}
            className={inputStyles}
            focusedClassName={styles.inputElement}
            tagProps={{ className: S.tag, classNameRemove: 'react-tagsinput-remove' }}
            inputProps={{ placeholder, className: S.input }}
            validationRegex={/^[^\s]+(\s+[^\s]+)*$/}
          />
        </div>
        {storeZips.map(storeWithZips => (<div key={`store_${storeWithZips.storeNum}`}>
          <StoresInput
            storeNumber={storeWithZips.storeNum}
            storeName={storeWithZips.storeName}
            removeStoreZips={this.removeChosenStoresZips}
            addStoreZips={this.addChosenStoresZips}
          />
          <div className={CTIStyles.zipsContainer}>
            {storeWithZips.coveredZips.length > 0 && storeWithZips.coveredZips.sort().map(zip =>
              (<ZipsInput
                key={zip}
                value={zip}
                removeZipCode={this.removeZipCode}
              />),
            )}
          </div>
        </div>))}
      </div>
      {error && <div className={errStyles}>
        <p className={S.errors}>{error}</p>
      </div>}
    </div>);
  }
}

CustomTaggedInput.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onCrewZipChange: PropTypes.func,
  placeholder: PropTypes.string,
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  crew: PropTypes.object,
  allWorkrooms: PropTypes.array,
  workroomChange: PropTypes.bool,
};

CustomTaggedInput.defaultProps = {
  label: '',
  id: '',
  errors: '',
  placeholder: '',
};

export default CustomTaggedInput;



// WEBPACK FOOTER //
// ./client/app/shared/components/TaggedInput/CustomTaggedInput/CTIIndex.jsx